/* ==========================================================================
   十页原型排版预览 · 基础交互层（第 1 批：全局基线）

   只做"让人看出这是原型"的基础交互，不接任何真实数据、不改任何排版：
     1) reveal(el) / collapse(el) —— 全站统一的显隐过渡（所有 .hidden 切换走这里）
     2) focusHeading(region)      —— 切视图后把焦点移到新区域标题，再滚动
     3) scrollToEl(el)            —— reduced-motion 时降级为 behavior:'auto'
     4) 键盘化：.check → role=checkbox + aria-checked；.year / .chip → role=button
     5) inert 内部标记与死链守护（不带任何可见的"演示"提示）

   配套样式见 assets/auxito-proto-shell-interactions.css。
   ========================================================================== */
(() => {
  'use strict';
  /* 埋点（assets/auxito-proto-shell-tracking.js）：业务确认成功 / 失败时才上报，脚本没加载时什么都不做 */
  const track = (method, ...args) => {
    const T = window.AuxitoTrack;
    try { if (T && typeof T[method] === 'function') T[method](...args); } catch (error) { /* 埋点失败不影响页面 */ }
  };

  const HIDDEN = 'hidden';
  const MS_EXPAND = 200;
  const INERT = 'data-apsp-inert';

  /* 看着可点、但原型里没有链路的目标 */
  const INERT_SELECTOR =
    'a[href="#"], .link, .rightlink, .filter, .year, .chip, .apsp-filter-item, .apsp-show-btn, .apsp-view-btn';
  const FOCUSABLE = /^(A|BUTTON|INPUT|SELECT|TEXTAREA|SUMMARY)$/;
  const reduceQuery =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
  const reduced = () => !!(reduceQuery && reduceQuery.matches);

  /* ------------------------------------------------------------------ *
   * 1) 统一 reveal / collapse
   * ------------------------------------------------------------------ */
  const seq = new WeakMap();
  const bump = (el) => {
    const n = (seq.get(el) || 0) + 1;
    seq.set(el, n);
    return n;
  };
  const isHidden = (el) => el.classList.contains(HIDDEN) || el.hasAttribute('hidden');
  const clearInline = (el) => {
    el.style.maxHeight = '';
    el.style.opacity = '';
    el.style.transform = '';
    el.removeAttribute('data-apsp-reveal');
  };

  function reveal(el) {
    if (!el) return;
    const token = bump(el);
    el.classList.remove(HIDDEN);
    el.removeAttribute('hidden');
    if (reduced() || !el.isConnected) {
      clearInline(el);
      return;
    }
    const height = Math.ceil(el.getBoundingClientRect().height) || el.scrollHeight;
    if (!height) {
      clearInline(el);
      return;
    }
    el.setAttribute('data-apsp-reveal', '');
    el.style.maxHeight = '0px';
    el.style.opacity = '0';
    el.style.transform = 'translateY(6px)';
    void el.offsetHeight; /* 先落一帧 0，保证 0 → 高度能真正过渡 */
    el.style.maxHeight = height + 'px';
    el.style.opacity = '1';
    el.style.transform = 'translateY(0)';
    window.setTimeout(() => {
      if (seq.get(el) === token) clearInline(el); /* 残留 max-height 会截断内容 */
    }, MS_EXPAND + 60);
  }

  function collapse(el) {
    if (!el || isHidden(el)) return;
    if (reduced()) {
      bump(el);
      el.classList.add(HIDDEN);
      el.setAttribute('hidden', ''); /* 同时写 hidden 属性：调用方用 el.hidden 判断开合时不会读错（01 more 二次展开失效） */
      clearInline(el);
      return;
    }
    const token = bump(el);
    const height = Math.ceil(el.getBoundingClientRect().height) || el.scrollHeight;
    el.setAttribute('data-apsp-reveal', '');
    el.style.maxHeight = height + 'px';
    el.style.opacity = '1';
    el.style.transform = 'translateY(0)';
    void el.offsetHeight;
    el.style.maxHeight = '0px';
    el.style.opacity = '0';
    el.style.transform = 'translateY(-6px)';
    window.setTimeout(() => {
      if (seq.get(el) !== token) return;
      el.classList.add(HIDDEN);
      el.setAttribute('hidden', '');
      clearInline(el);
    }, MS_EXPAND + 60);
  }

  /* ------------------------------------------------------------------ *
   * 2) 焦点与滚动
   * ------------------------------------------------------------------ */
  /* 主题页头 #shopify-section-static-header 是 position:sticky（z 1070）：直接 scrollIntoView
     block:'start' 会把目标标题压在页头下面（BUG17 实测 ≤1024 看不见 Browse by Make 标题）。
     对齐顶部时扣掉当前贴顶的 sticky / fixed 页头高度再留 12px。 */
  function stickyOffset() {
    let offset = 0;
    document.querySelectorAll('#shopify-section-static-header, .site-header-wrapper').forEach((el) => {
      const cs = getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') return;
      const rect = el.getBoundingClientRect();
      if (rect.top <= 1 && rect.bottom > offset) offset = rect.bottom;
    });
    return offset;
  }

  function scrollToEl(el, opts) {
    if (!el || typeof el.scrollIntoView !== 'function') return;
    const behavior = reduced() ? 'auto' : 'smooth';
    const block = (opts && opts.block) || 'start';
    if (block === 'start') {
      const top = el.getBoundingClientRect().top + window.scrollY - stickyOffset() - 12;
      window.scrollTo({ top: Math.max(0, top), behavior });
      return;
    }
    el.scrollIntoView({ behavior, block });
  }

  function focusHeading(region, opts) {
    const root = typeof region === 'string' ? document.querySelector(region) : region;
    if (!root) return null;
    const head = root.querySelector('h1, h2, h3') || root;
    if (!head.hasAttribute('tabindex') && !FOCUSABLE.test(head.tagName)) {
      head.setAttribute('tabindex', '-1');
    }
    try {
      head.focus({ preventScroll: true });
    } catch (error) {
      try {
        head.focus();
      } catch (error2) {
        /* 忽略：焦点移动失败不影响视图切换 */
      }
    }
    if (!opts || opts.scroll !== false) scrollToEl(root, opts);
    return head;
  }

  /* ------------------------------------------------------------------ *
   * 3) 键盘化（视觉零变化）
   * ------------------------------------------------------------------ */
  function syncChecked(el) {
    if (el && el.getAttribute('role') === 'checkbox') {
      el.setAttribute('aria-checked', el.classList.contains('on') ? 'true' : 'false');
    }
  }

  function keyboardize(root) {
    root.querySelectorAll('.check').forEach((el) => {
      if (!el.hasAttribute('role')) el.setAttribute('role', 'checkbox');
      if (!el.hasAttribute('tabindex') && !FOCUSABLE.test(el.tagName)) {
        el.setAttribute('tabindex', '0');
      }
      syncChecked(el);
    });
    root.querySelectorAll('.year, .chip').forEach((el) => {
      if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
      if (!el.hasAttribute('tabindex') && !FOCUSABLE.test(el.tagName)) {
        el.setAttribute('tabindex', '0');
      }
    });
  }

  /* 预设按钮等会直接改 .check 的 class，aria-checked 必须跟着走 */
  function watchChecked(root) {
    const checks = root.querySelectorAll('.check');
    if (!checks.length || typeof MutationObserver !== 'function') return;
    const observer = new MutationObserver((list) => {
      list.forEach((mutation) => syncChecked(mutation.target));
    });
    checks.forEach((el) => observer.observe(el, { attributes: true, attributeFilter: ['class'] }));
  }

  /* ------------------------------------------------------------------ *
   * 4) inert 内部标记（只给脚本用，不产生可见效果）
   * ------------------------------------------------------------------ */
  function markInert(root) {
    root.querySelectorAll(INERT_SELECTOR).forEach((el) => {
      /* 在真正的链接（非 # 锚）里面的都不标记，避免挡住真实跳转
         （运营 2026-09-29：04 LED Bulbs 卡整卡是链接，点卡内「Browse Bulbs →」文字曾被当成死链拦掉） */
      if (el.closest('a[href]:not([href="#"])')) return;
      el.setAttribute(INERT, '');
      if (!el.hasAttribute('tabindex') && !FOCUSABLE.test(el.tagName)) {
        el.setAttribute('tabindex', '0');
      }
    });
  }


  /* ------------------------------------------------------------------ *
   * 5) 死链守护（href="#" 不再把页面弹回顶部）
   * ------------------------------------------------------------------ */
  function bindInertGuards() {
    document.addEventListener(
      'click',
      (event) => {
        const el = event.target && event.target.closest && event.target.closest('[' + INERT + ']');
        if (el) event.preventDefault(); /* href="#" 的死链不再把页面弹回顶部 */
      },
      true
    );
    document.addEventListener(
      'keydown',
      (event) => {
        const el = event.target && event.target.closest && event.target.closest('[' + INERT + ']');
        if (!el || el.getAttribute('role') !== 'button') return;
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        event.preventDefault(); /* 已经声明成 button 的元素不要用空格翻页 */
      },
      true
    );
  }

  /* ------------------------------------------------------------------ *
   * 6) 第 2 批：页面级交互（04 / 05 / 06 / 07）
   *    04/05/07 的状态机仍留在各自的内联脚本里（深链要零闪烁）；
   *    这里只做"把 inert 元素接管成真控件"与 06 的定位/链接。
   * ------------------------------------------------------------------ */
  const norm = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const T_ = () => window.AuxitoProtoShellInteractions;
  const isNativeControl = (el) =>
    el.tagName === 'BUTTON' || (el.tagName === 'A' && el.hasAttribute('href'));

  /* 接管：撤掉 batch 1 的 inert 标记，换成真控件语义 */
  function claim(el, role) {
    if (!el) return null;
    el.removeAttribute(INERT);
    if (role) el.setAttribute('role', role);
    if (!el.hasAttribute('tabindex') && !FOCUSABLE.test(el.tagName)) {
      el.setAttribute('tabindex', '0');
    }
    return el;
  }

  /* 卡片被接管成控件后，卡内原本 inert 的 .link 文案降级为装饰文本，
     否则卡内文案会被当成独立可点目标，和卡片本身的可点区域打架。 */
  function releaseInner(container) {
    container.querySelectorAll('[' + INERT + ']').forEach((inner) => {
      inner.removeAttribute(INERT);
      inner.removeAttribute('tabindex');
    });
  }

  function bindActivate(el, handler) {    if (!el || el.__apspActivate) return;
    el.__apspActivate = true;
    el.addEventListener('click', (event) => {
      event.preventDefault();
      handler(event);
    });
    if (isNativeControl(el)) return; /* button / 真链接自带 Enter/Space */
    el.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      handler(event);
    });
  }

  function gotoView(view, params) {
    /* 正式网址模式（车型索引存在）：直接去标准网址（如 02 → /pages/vehicle/{mh}），不再拼预览网址 ?view=proto-shell-NN
       （运营 2026-09-29：01 Popular Vehicles 点进的是预览版 02，缺 Browse by Year） */
    const cf = ctx();
    const screen = /^proto-shell-(\d\d)$/.exec(view);
    if (cf && cf.formal && screen) {
      const target = (params && params.vehicle && cf.list.filter((v) => v.key === params.vehicle)[0]) || cf.current;
      const next = cf.urlFor(screen[1], target, { position: (params && params.position) || '', state: (params && params.state) || '' });
      if (next) {
        location.href = next;
        return;
      }
    }
    const query = new URLSearchParams();
    query.set('view', view);
    Object.keys(params || {}).forEach((key) => {
      if (params[key]) query.set(key, params[key]);
    });
    const c = ctx();
    if (c && !query.has('vehicle') && !query.has('year')) query.set('vehicle', c.current.key);
    location.href = location.pathname + '?' + query.toString();
  }

  /* 04：8 张灯位卡 → 05（带 ?position=）。没有对应灯位的卡保持 inert。 */
  const POSITION_SLUGS = {
    'headlight bulbs': 'headlight',
    'fog & driving light bulbs': 'fog-driving',
    'turn signal bulbs': 'turn-signal',
    'brake & tail light bulbs': 'brake-tail',
    'reverse & backup bulbs': 'reverse-backup',
    'interior & dome bulbs': 'interior-dome',
    'license plate bulbs': 'license-plate',
    'parking, drl & side marker': 'parking-drl-marker',
  };

  /* 旧 05 页已由 Application PLP · Type（/pages/search-result?view=search-result-plp-type）取代：
     卡片跳转读 section 输出的 [data-apsp-plp-urls]（snippets/auxito-proto-shell-plp-url.liquid 生成，
     别名的值是目标 key）。读不到时退回旧 05 页，避免卡片点了没反应。 */
  let plpMap = null;
  function plpUrl(key) {
    if (!plpMap) {
      try {
        const node = document.querySelector('[data-apsp-plp-urls]');
        plpMap = node ? JSON.parse(node.textContent) : {};
      } catch (error) {
        plpMap = {};
      }
    }
    let value = plpMap[key] || '';
    if (value && value.charAt(0) !== '/') value = plpMap[value] || '';
    return value || plpMap['high-beam'] || '';
  }
  function gotoPlp(key, legacyParams) {
    const url = plpUrl(key);
    if (url) location.href = url;
    else gotoView('proto-shell-05', legacyParams);
  }

  function wirePositionCards(root) {
    const view = root.querySelector('#bulbView');
    if (!view) return;
    view.querySelectorAll('.card').forEach((card) => {
      const heading = card.querySelector('h3');
      const slug = POSITION_SLUGS[norm(heading && heading.textContent)];
      if (!slug) return;
      releaseInner(card);
      claim(card, 'link');
      bindActivate(card, () => gotoPlp(slug, { position: slug }));
    });
  }

  /* 06：Quick Answer 卡定位表行 + 两个真链接 + 表格可滚提示 */
  function locateRow(row) {
    if (!row) return;
    const place = (behavior) => {
      const rect = row.getBoundingClientRect();
      window.scrollTo({
        top: Math.max(0, window.scrollY + rect.top - window.innerHeight * 0.38),
        behavior: behavior,
      });
    };
    place(reduced() ? 'auto' : 'smooth');
    window.setTimeout(() => {
      place('auto'); /* 平滑滚动落点有偏差，落定后再校准一次，确保停在视口 38% */
      if (reduced()) return; /* reduced-motion：只滚动，不闪 */
      row.classList.remove('is-located');
      void row.offsetHeight;
      row.classList.add('is-located');
      window.setTimeout(() => row.classList.remove('is-located'), 1400);
    }, 520);
  }

  const GUIDE_TYPE_BY_CARD = [
    [/halogen/i, 'halogen'],
    [/hid/i, 'hid'],
    [/led|assembly/i, 'unknown'],
  ];

  function wireFitmentGuide(root) {
    const table = root.querySelector('table.table');
    const rows = table ? [...table.querySelectorAll('tbody tr')] : [];
    rows.forEach((row, index) => {
      if (!row.id) row.id = 'apsp-fitment-row-' + (index + 1);
    });

    /* Quick Answer：卡整块作为"定位到表行"的控件；命中不到行就不接管。
       运营 2026-09-29：06 的 Quick Answer 已改成直接进 05 的链接（a.card），链接卡不再接管 */
    root.querySelectorAll('.grid.g4 .card:not(a)').forEach((card) => {
      const heading = card.querySelector('h3');
      const label = norm(heading && heading.textContent);
      if (!label) return;
      const row = rows.find((candidate) => {
        const cell = candidate.querySelector('td');
        return cell && norm(cell.textContent).includes(label);
      });
      if (!row) return;
      releaseInner(card);
      claim(card, 'button');
      card.setAttribute('aria-controls', row.id);
      bindActivate(card, () => locateRow(row));
    });

    /* Before You Buy：3 张卡 → 05?type=halogen|hid|unknown */
    const beforeHeading = [...root.querySelectorAll('h2')].find((h) =>
      /before you buy/i.test(h.textContent || '')
    );
    const beforeSection = beforeHeading && beforeHeading.closest('section');
    if (beforeSection) {
      beforeSection.querySelectorAll('.card').forEach((card) => {
        const heading = card.querySelector('h3');
        const text = (heading && heading.textContent) || '';
        const match = GUIDE_TYPE_BY_CARD.find((rule) => rule[0].test(text));
        if (!match) return;
        claim(card, 'link');
        bindActivate(card, () => gotoPlp('high-beam', { type: match[1] })); /* 本车只有卤素大灯，新 PLP 按大灯灯位展示 */
      });
    }

    /* 可横滚时才提示右缘渐隐，滚动一次后不再出现 */
    root.querySelectorAll('.tablewrap').forEach((wrap) => {
      let used = false;
      const sync = () => {
        if (used) return;
        const scrollable = wrap.scrollWidth - wrap.clientWidth > 4;
        wrap.classList.toggle('is-scrollable', scrollable);
      };
      sync();
      wrap.addEventListener(
        'scroll',
        () => {
          used = true;
          wrap.classList.remove('is-scrollable');
        },
        { passive: true }
      );
      window.addEventListener('resize', sync, { passive: true });
    });
  }


  /* ------------------------------------------------------------------ *
   * 7) BUG跟进(9.17) 批次：01 / 02 页的运营改动
   * ------------------------------------------------------------------ */
  const YMM_KEY = 'apsp:ymm';
  const PLACEHOLDER = /^(year|make|model)$/i;

  function readYmm() {
    try {
      return JSON.parse(sessionStorage.getItem(YMM_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }
  function writeYmm(value) {
    try {
      sessionStorage.setItem(YMM_KEY, JSON.stringify(value));
    } catch (error) {
      /* 隐私模式下写不进去就算了，不影响页面 */
    }
  }
  const ymmComplete = (v) =>
    !!(v && !PLACEHOLDER.test(v.year || '') && !PLACEHOLDER.test(v.make || '') && !PLACEHOLDER.test(v.model || ''));

  /* 公共车型上下文（assets/auxito-proto-shell-context.js，2026-09-24 上线框架） */
  const ctx = () => window.AuxitoProtoShellVehicle || null;

  function withYmm(url, value) {
    if (!ymmComplete(value)) return url;
    const c = ctx();
    if (c) {
      const v = c.match(value.year, value.make, value.model);
      if (v) return c.withVehicle(url, v);
    }
    const [base, hash] = url.split('#');
    const query = new URLSearchParams((base.split('?')[1] || ''));
    query.set('year', value.year);
    query.set('make', value.make);
    query.set('model', value.model);
    return base.split('?')[0] + '?' + query.toString() + (hash ? '#' + hash : '');
  }

  /* 按车型清单填 Year / Make / Model 三个下拉并联动（选年份后品牌、车型只列有效组合）；prefill 为要预选的车型 */
  function fillYmmSelects(selects, prefill) {
    const c = ctx();
    if (!c || selects.length < 3) return;
    const [ySel, mSel, dSel] = selects;
    const uniq = (arr) => arr.filter((v, i) => arr.indexOf(v) === i);
    const fill = (sel, values, keep, label) => {
      sel.innerHTML = '';
      const blank = document.createElement('option');
      blank.value = label; blank.textContent = label;
      sel.appendChild(blank);
      values.forEach((v) => { const o = document.createElement('option'); o.value = v; o.textContent = v; sel.appendChild(o); });
      sel.value = values.indexOf(keep) >= 0 ? keep : label;
    };
    const years = () => uniq(c.list.map((v) => v.year)).sort().reverse();
    const makes = () => uniq(c.list.filter((v) => PLACEHOLDER.test(ySel.value) || v.year === ySel.value).map((v) => v.make)).sort();
    const models = () => uniq(c.list.filter((v) => (PLACEHOLDER.test(ySel.value) || v.year === ySel.value) && (PLACEHOLDER.test(mSel.value) || v.make === mSel.value)).map((v) => v.model)).sort();
    /* 返回重填用（01 返回页面时解除锁定），不重复绑定事件 */
    const refill = (next) => {
      fill(ySel, years(), next ? next.year : '', 'Year');
      fill(mSel, makes(), next ? next.make : '', 'Make');
      fill(dSel, models(), next ? next.model : '', 'Model');
    };
    refill(prefill);
    const catalogMode = () => ySel.dataset.catalog === '1'; /* 01 全站目录模式（非上线车型）：年份已按该车型过滤，Make / Model 固定 */
    ySel.addEventListener('change', () => { if (catalogMode()) return; fill(mSel, makes(), mSel.value, 'Make'); fill(dSel, models(), dSel.value, 'Model'); });
    mSel.addEventListener('change', () => { if (catalogMode()) return; fill(dSel, models(), dSel.value, 'Model'); });
    return refill;
  }

  /* ---- 01 页 ---- */
  function wireScreen01(root) {
    const heroForm = root.querySelector('.hero-form');
    const selects = heroForm ? [...heroForm.querySelectorAll('select')] : [];
    const prompt = root.querySelector('.apsp-ymm-prompt');
    const confirmBtn = root.querySelector('[data-ymm-confirm]');
    let pendingTask = null;
    let sheet = null;
    let backdrop = null;

    const currentYmm = () => ({
      year: selects[0] ? selects[0].value : '',
      make: selects[1] ? selects[1].value : '',
      model: selects[2] ? selects[2].value : '',
    });

    /* 横幅 Year / Make / Model 列全站所有车型（运营 2026-09-29：返回 01 等情况下不应被限制在上线车型或某台车上）：
       按全站车型目录（AuxitoVehicleUtils，与页头选车器同一份）填三个下拉并联动，见 initCatalogYmm；
       预选顺序：本页本次会话选过的 > 网址或已保存的上线车型 > 页头保存的车。目录脚本不在时退回只列上线车型（fillYmmSelects）。 */
    const c01 = ctx();
    const modalFields = [...root.querySelectorAll('[data-vehicle-field]')];
    /* My Garage「Add Another Vehicle」弹窗：目录脚本在时与横幅一样列全站车型（运营 2026-10-13，见下方 wireModalCatalog），否则只列上线车型 */
    if (c01 && modalFields.length === 3 && !(window.AuxitoVehicleUtils && selects.length === 3)) fillYmmSelects(modalFields, null);
    const saved = readYmm();
    let ymmPrefill = ymmComplete(saved) ? saved : null;
    if (!ymmPrefill && c01 && c01.source !== 'default') ymmPrefill = { year: String(c01.current.year), make: c01.current.make, model: c01.current.model };
    if (!ymmPrefill) {
      try {
        const head = (JSON.parse(localStorage.getItem('auxito_vehicles') || 'null') || {}).current;
        if (head && head.year && head.make && head.model) ymmPrefill = { year: String(head.year), make: head.make, model: head.model };
      } catch (error) { /* 读不到保存的车就不预选 */ }
    }
    const useCatalog = !!(window.AuxitoVehicleUtils && selects.length === 3);
    if (!useCatalog && c01) {
      fillYmmSelects(selects, c01.source === 'default' ? null : c01.current);
      if (ymmComplete(saved)) {
        selects.forEach((sel, i) => {
          const want = [saved.year, saved.make, saved.model][i];
          if (want && [...sel.options].some((o) => o.value === want || o.textContent.trim() === want)) {
            sel.value = [...sel.options].find((o) => o.value === want || o.textContent.trim() === want).value;
          }
        });
      }
    }
    /* 记录 12：填了 YMM 就整页保存（不点确认也保存） */
    selects.forEach((sel) =>
      sel.addEventListener('change', () => {
        writeYmm(currentYmm());
        if (prompt && ymmComplete(currentYmm())) prompt.hidden = true;
        root.querySelectorAll('[data-task-link]').forEach((card) => {
          if (card.dataset.pendingLabel) {
            const link = card.querySelector('.link');
            if (link) link.textContent = card.dataset.baseLabel || link.textContent;
            delete card.dataset.pendingLabel;
          }
        });
      })
    );

    function attention(region) {
      const target = region || heroForm;
      if (!target) return;
      scrollToEl(target, { block: 'center' });
      if (prompt) prompt.hidden = false;
      target.classList.add('apsp-ymm-highlight');
      if (!reduced()) {
        target.classList.remove('apsp-ymm-shake');
        void target.offsetWidth;
        target.classList.add('apsp-ymm-shake');
      }
      window.setTimeout(() => {
        target.classList.remove('apsp-ymm-highlight', 'apsp-ymm-shake');
      }, 1600);
      const first = selects[0];
      if (first) {
        try {
          first.focus({ preventScroll: true });
        } catch (error) {
          first.focus();
        }
      }
    }

    function ensureSheet() {
      if (sheet && sheet.isConnected) return sheet;
      backdrop = document.createElement('div');
      backdrop.className = 'apsp-sheet-backdrop';
      backdrop.hidden = true;
      sheet = document.createElement('div');
      sheet.className = 'apsp-ymm-sheet';
      sheet.hidden = true;
      sheet.innerHTML =
        '<h4>Select Your Vehicle First</h4>' +
        '<p>Enter your Year, Make &amp; Model to see options that fit your vehicle.</p>' +
        '<button class="btn full" type="button" data-sheet-select>Select Vehicle</button>';
      document.body.appendChild(backdrop);
      document.body.appendChild(sheet);
      backdrop.addEventListener('click', closeSheet);
      sheet.querySelector('[data-sheet-select]').addEventListener('click', () => {
        closeSheet();
        attention(null);
      });
      return sheet;
    }
    function openSheet() {
      ensureSheet();
      backdrop.hidden = false;
      sheet.hidden = false;
    }
    function closeSheet() {
      if (sheet) sheet.hidden = true;
      if (backdrop) backdrop.hidden = true;
    }

    /* 运营 2026-09-29：本次只上线 Silverado。横幅下拉列全站车型：选到上线车型（车型清单里的 Silverado）进新车型页；
       其他车确认后进旧版车型首页 /pages/vehicle?year=&make=&model=（临时 03），再从那里选品类 / 灯位进 05，
       同时隐藏 LED Kit / Guides & Help 两张任务卡。Popular Vehicles / Browse by Make 里的非上线车型：带入 Make + Model，
       Year 先只列该车型有数据的年份；不锁定，用户改品牌或车型、或按浏览器返回本页时恢复全部年份。 */
    const vu = () => window.AuxitoVehicleUtils || null;
    const flat = (value) => norm(value).replace(/[^a-z0-9]/g, '');
    /* 运营 2026-10-13：Build an Off-Road Setup（10）对所有车型开放：非上线车型也进 10，网址带 year / make / model（10 页按 context.js 的 outside 显示这台车）；
       上线车型照旧走 withYmm（正式网址模式下是固定页面 + vehicle=） */
    const OFFROAD_TASK = '?view=proto-shell-10';
    const offroadUrl = (v) => {
      /* 进 10 的非上线车型也存进全站车型（页头 / My Garage 显示同一台车） */
      const store = window.Auxito && window.Auxito.Vehicle && window.Auxito.Vehicle.Storage;
      try { if (store && typeof store.addVehicle === 'function') store.addVehicle({ year: String(v.year), make: v.make, model: v.model }); } catch (error) { /* 存不了不影响跳转 */ }
      const c = ctx();
      let base = OFFROAD_TASK;
      if (c && c.formal && typeof c.urlFor === 'function') base = c.urlFor('10', c.current) || base;
      const u = new URL(base, window.location.href);
      u.searchParams.delete('vehicle');
      u.searchParams.set('year', v.year);
      u.searchParams.set('make', v.make);
      u.searchParams.set('model', v.model);
      return u.pathname + u.search;
    };
    const legacyUrl = (v) => {
      const u = vu();
      if (u && typeof u.buildVehiclePageUrl === 'function') return u.buildVehiclePageUrl(v, '/pages/vehicle');
      return '/pages/vehicle?' + new URLSearchParams({ year: v.year, make: v.make, model: v.model }).toString();
    };
    const setOptions = (sel, values, label, keep) => {
      sel.innerHTML = '';
      const blank = document.createElement('option');
      blank.value = label; blank.textContent = label;
      sel.appendChild(blank);
      values.forEach((item) => {
        const o = document.createElement('option');
        o.value = item.value; o.textContent = item.text || item.value;
        sel.appendChild(o);
      });
      const hit = values.find((item) => flat(item.value) === flat(keep));
      sel.value = hit ? hit.value : label;
    };
    const catalogMessage = (text) => {
      if (!prompt) return;
      const span = prompt.querySelector('span');
      if (span) span.textContent = text;
      prompt.hidden = false;
    };
    const val = (sel) => (sel && !PLACEHOLDER.test(sel.value) ? sel.value : '');
    /* 上线车型判断：完整 YMM 按车型清单精确匹配；只选了品牌 / 品牌 + 车型时看清单里有没有 */
    const launchMatch = (v) => { const c = ctx(); return c && ymmComplete(v) ? c.match(v.year, v.make, v.model) : null; };
    const launchHas = (make, model) => {
      const c = ctx();
      return !!(c && c.list.some((x) => flat(x.make) === flat(make) && (!model || flat(x.model) === flat(model) || flat(x.lib_model) === flat(model))));
    };
    /* LED Kit（09 只有 Silverado 数据）与 Guides & Help（07 安装帮助）：选到非上线车型时不显示，只留 Shop Compatible Parts 与 Off-Road Setup */
    const CATALOG_HIDDEN_TASKS = ['?view=proto-shell-09', '?view=proto-shell-07'];
    const syncCatalogTasks = () => {
      const v = currentYmm();
      const make = val(selects[1]);
      const model = val(selects[2]);
      let hide = false;
      if (ymmComplete(v)) hide = !launchMatch(v);
      else if (make) hide = !launchHas(make, model);
      /* 09 只有部分车型有 Builder 数据（现在只有 2003 Silverado 2500 HD）：选到已上线但没有 Builder 的车，LED Kit 也不显示（运营 2026-10-14） */
      const picked = ymmComplete(v) ? launchMatch(v) : null;
      const c = ctx();
      const noBuilder = !!(picked && c && typeof c.hasBuilder === 'function' && !c.hasBuilder(picked));
      root.querySelectorAll('[data-task-link]').forEach((card) => {
        const link = card.getAttribute('data-task-link');
        if (CATALOG_HIDDEN_TASKS.indexOf(link) >= 0) card.hidden = hide || (link === '?view=proto-shell-09' && noBuilder);
      });
    };
    let catIndex = null;
    let yearFilter = null; /* 带入车型时只列该车型有数据的年份 */
    let modelHint = null; /* 带入车型、还没选年份时，Model 先只放这一项 */
    let modelSeq = 0;
    const ensureIndex = async () => {
      if (!catIndex) catIndex = await vu().getVehicleData();
      return catIndex;
    };
    const catYears = () => (catIndex.years || []).map(String).filter((y) => /^\d{4}$/.test(y)).sort((x, y) => y - x);
    const catMakes = (year) => {
      const u = vu();
      const seen = new Map();
      (year ? [year] : catYears()).forEach((y) => (u.getCaseInsensitivePropertyValue(catIndex.makesByYear, y) || []).forEach((m) => {
        if (!seen.has(flat(m))) seen.set(flat(m), m);
      }));
      return [...seen.values()].sort((x, y) => x.localeCompare(y));
    };
    const afterYmmChange = () => {
      writeYmm(currentYmm());
      syncCatalogTasks();
    };
    const renderYears = (keep) => setOptions(selects[0], (yearFilter || catYears()).map((y) => ({ value: y })), 'Year', keep);
    const renderMakes = (keep) => setOptions(selects[1], catMakes(val(selects[0])).map((m) => ({ value: m })), 'Make', keep);
    async function renderModels(keep) {
      const seq = ++modelSeq;
      const year = val(selects[0]);
      const make = val(selects[1]);
      if (!year || !make) {
        const hint = modelHint && make && flat(modelHint.make) === flat(make) ? [{ value: modelHint.model }] : [];
        setOptions(selects[2], hint, 'Model', keep);
        afterYmmChange();
        return;
      }
      /* 新年份 / 品牌的车型列表加载完之前先清掉旧车型并禁用（PR #55 第五轮审查：否则能提交「新年份 + 旧车型」）；加载完按 keep 恢复 */
      setOptions(selects[2], [], 'Model', '');
      selects[2].disabled = true;
      try {
        const data = await vu().getYearVehicleData(year);
        if (seq !== modelSeq) return;
        /* 目录里写作 Silverado 2500 Hd：显示成 HD，值保留目录原文 */
        setOptions(selects[2], vu().getAvailableModels(data, make).map((m) => ({ value: m, text: m.replace(/\bHd\b/g, 'HD') })), 'Model', keep);
      } catch (error) {
        if (seq === modelSeq) { track('result', 'load_failed', { apsp_module: 'hero' }); setOptions(selects[2], [], 'Model', ''); }
      } finally {
        if (seq === modelSeq) selects[2].disabled = false;
      }
      if (seq === modelSeq) afterYmmChange();
    }
    async function initCatalogYmm(prefill) {
      try {
        await ensureIndex();
        renderYears(prefill ? prefill.year : '');
        renderMakes(prefill ? prefill.make : '');
        await renderModels(prefill ? prefill.model : '');
      } catch (error) {
        track('result', 'load_failed', { apsp_module: 'hero' });
        if (c01) fillYmmSelects(selects, c01.source === 'default' ? null : c01.current);
      }
    }
    if (useCatalog) {
      /* 目录没读到时（initCatalogYmm 已退回只列上线车型）这三个联动不处理，交给 fillYmmSelects 自己的联动 */
      selects[0].addEventListener('change', () => { if (!catIndex) return; renderMakes(val(selects[1])); renderModels(val(selects[2])); });
      selects[1].addEventListener('change', () => {
        if (!catIndex) return;
        if (yearFilter) { yearFilter = null; modelHint = null; renderYears(val(selects[0])); }
        renderModels('');
      });
      selects[2].addEventListener('change', () => {
        if (!catIndex) return;
        if (yearFilter && modelHint && flat(val(selects[2])) !== flat(modelHint.model)) { yearFilter = null; modelHint = null; renderYears(val(selects[0])); }
        afterYmmChange();
      });
      initCatalogYmm(ymmPrefill);
      /* 按浏览器返回本页（往返缓存原样恢复）：解除带入车型时的年份限制，三个下拉恢复列全部车型，保留已选的值 */
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted || !catIndex) return;
        catalogSeq++;
        yearFilter = null; /* 已选的品牌 / 车型保留（还没选年份时 Model 仍显示带入的车型），只放开年份 */
        renderYears(val(selects[0]));
        renderMakes(val(selects[1]));
        renderModels(val(selects[2]));
        if (prompt) prompt.hidden = true;
      });
    }
    let catalogSeq = 0;
    async function pickFromCatalog(make, model, label) {
      const u = vu();
      if (!u || selects.length < 3) { location.href = '/pages/vehicle'; return; }
      const seq = ++catalogSeq;
      attention(null);
      catalogMessage('Loading years for ' + (label || make + ' ' + model) + '…');
      try {
        await ensureIndex();
        const years = catYears().filter((y) => (u.getCaseInsensitivePropertyValue(catIndex.makesByYear, y) || []).some((m) => flat(m) === flat(make)));
        let failed = 0;
        const found = (await Promise.all(years.map((y) => u.getYearVehicleData(y).then((data) => {
          const makes = u.getCaseInsensitivePropertyValue(catIndex.makesByYear, y) || [];
          const catMake = makes.find((m) => flat(m) === flat(make));
          const catModel = u.getAvailableModels(data, catMake).find((m) => flat(m) === flat(model));
          return catModel ? { year: y, make: catMake, model: catModel } : null;
        }).catch(() => { failed += 1; return null; })))).filter(Boolean);
        if (seq !== catalogSeq) return;
        if (!found.length) { track('result', failed ? 'load_failed' : 'no_match', { apsp_module: 'browse-by-make' }); /* 有年份数据没读到时算加载失败，不算查无此车 */ catalogMessage("We couldn't find this model in our vehicle list. Please use the Year, Make & Model search at the top of the page."); return; }
        yearFilter = found.map((f) => String(f.year));
        modelHint = { make: found[0].make, model: found[0].model };
        renderYears('');
        renderMakes(found[0].make);
        await renderModels(found[0].model);
        catalogMessage('Choose your ' + (label || make + ' ' + model) + ' year to continue.');
        attention(null);
      } catch (error) {
        if (seq === catalogSeq) { track('result', 'load_failed', { apsp_module: 'browse-by-make' }); catalogMessage('Vehicle list is unavailable right now. Please try again.'); }
      }
    }
    /* Browse by Make 里的非上线车型（context.js 标记 data-apsp-catalog="Make|Model"）：事件委托，不依赖两个脚本的加载顺序 */
    const onCatalogActivate = (event) => {
      const el = event.target.closest && event.target.closest('[data-apsp-catalog]');
      if (!el || !root.contains(el)) return;
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      const [make, model] = el.getAttribute('data-apsp-catalog').split('|');
      pickFromCatalog(make, model, make + ' ' + model);
    };
    root.addEventListener('click', onCatalogActivate);
    root.addEventListener('keydown', onCatalogActivate);

    /* 记录 19：四张任务卡必须填完整 YMM 才能跳转 */
    root.querySelectorAll('[data-task-link]').forEach((card) => {
      const base = card.getAttribute('data-task-link');
      const link = card.querySelector('.link');
      if (link) card.dataset.baseLabel = link.textContent;
      claim(card, 'link');
      bindActivate(card, () => {
        if (selects[2] && selects[2].disabled) return; /* 车型列表加载中 */
        const value = currentYmm();
        if (!ymmComplete(value)) {
          pendingTask = base;
          if (window.matchMedia('(max-width: 600px)').matches) openSheet();
          else attention(null);
          return;
        }
        track('vehicleSelected', 'task_card', value.year, value.make, value.model, launchMatch(value) ? 'yes' : 'no');
        if (!launchMatch(value)) { location.href = base === OFFROAD_TASK ? offroadUrl(value) : legacyUrl(value); return; }
        location.href = withYmm(base, value);
      });
    });

    /* 记录 12：Confirm Vehicle → 有 pending 任务则去任务页，否则去 03 */
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => {
        if (selects[2] && selects[2].disabled) return; /* 车型列表加载中 */
        const value = currentYmm();
        if (!ymmComplete(value)) {
          attention(null);
          return;
        }
        writeYmm(value);
        if (prompt) prompt.hidden = true;
        track('vehicleSelected', pendingTask ? 'task_card' : 'hero', value.year, value.make, value.model, launchMatch(value) ? 'yes' : 'no');
        if (!launchMatch(value)) { location.href = pendingTask === OFFROAD_TASK ? offroadUrl(value) : legacyUrl(value); return; }
        const cc = ctx();
        const picked = cc ? cc.match(value.year, value.make, value.model) : null;
        if (picked) cc.saveVehicle(picked);
        location.href = withYmm(pendingTask || '?view=proto-shell-03', value);
      });
    }

    /* 记录 16②：View all vehicles → 滚到底部 Browse by Make */
    root.querySelectorAll('[data-apsp-module="popular-vehicles"] .rightlink').forEach((el) => {
      const target = root.querySelector('[data-apsp-module="browse-by-make"] h2'); /* 按模块 ID 找，标题可在主题编辑器改（2.0.14） */
      if (!target) return;
      claim(el, 'button');
      bindActivate(el, () => focusHeading(target.closest('section') || target));
    });

    /* 记录 16③：Popular Vehicles 的 model 卡 → 02 Model Hub */
    const popular = root.querySelector('[data-apsp-module="popular-vehicles"] h2');
    if (popular) {
      const section = popular.closest('section');
      section.querySelectorAll('.card').forEach((card) => {
        /* 车型清单（上线框架）：卡片名称对应清单里的车型才可点，进入该车型的 02；不在上线范围的车型先隐藏 */
        const c = ctx();
        const label = norm((card.querySelector('b') || card).textContent);
        const hit = c ? c.list.filter((v) => label === norm(v.make + ' ' + v.model) || label === norm(v.model)).sort((a, b) => b.year - a.year)[0] : null;
        const make = card.getAttribute('data-popular-make');
        const model = card.getAttribute('data-popular-model');
        releaseInner(card);
        claim(card, 'link');
        if (c && !hit) {
          if (!make || !model) { card.hidden = true; return; }
          bindActivate(card, () => pickFromCatalog(make, model, (card.querySelector('b') || card).textContent.trim()));
          return;
        }
        bindActivate(card, () => gotoView('proto-shell-02', hit ? { vehicle: hit.key } : null));
      });
    }

    /* 记录 21：Browse by Make 的 more → 展开该 make 的 model 列表 */
    root.querySelectorAll('[data-make-toggle]').forEach((btn) => {
      const card = btn.closest('[data-make-card]'); /* 在本卡片内找：block 可能有两张同品牌卡，品牌名也不进选择器 */
      const panel = card ? card.querySelector('[data-make-models]') : null;
      if (!panel) return;
      claim(btn, 'button');
      bindActivate(btn, () => {
        const open = btn.getAttribute('aria-expanded') !== 'true'; /* 以按钮状态为准，不读 panel.hidden（收起后曾经读错，二次展开失效） */
        if (open) reveal(panel);
        else collapse(panel);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    /* 01 Browse by Make 收起态：车型与 more 同一行，放不下时从最后一个车型开始整项隐藏（运营 2026-10-28：卡片只两行、more 不另起一行） */
    const fitMakeLines = () => root.querySelectorAll('[data-make-fit]').forEach((list) => {
      const items = [...list.children];
      items.forEach((el) => el.classList.remove('apsp-make-drop'));
      for (let i = items.length - 1; i > 0 && list.scrollWidth > list.clientWidth + 1; i -= 1) {
        if (items[i].offsetParent !== null || getComputedStyle(items[i]).display !== 'none') items[i].classList.add('apsp-make-drop');
      }
    });
    if (root.querySelector('[data-make-fit]')) {
      fitMakeLines();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitMakeLines);
      if (window.ResizeObserver) {
        let fitRaf = 0;
        const ro = new ResizeObserver(() => { cancelAnimationFrame(fitRaf); fitRaf = requestAnimationFrame(fitMakeLines); });
        root.querySelectorAll('[data-make-fit]').forEach((list) => ro.observe(list.parentNode));
      }
    }

    /* My Garage（上线框架）：显示全站已保存的车（与页头、06、search-result 同一份），最多 3 台，不再显示演示车；
       在上线车型清单里的车点击进入它的 03 */
    const garageHead = root.querySelector('[data-apsp-module="my-garage"] h2');
    const garageGrid = garageHead ? garageHead.closest('section').querySelector('.grid') : null;
    const cG = ctx();
    const renderGarage = () => {
      if (!garageGrid || !cG) return;
      let stored = null;
      try { stored = JSON.parse(localStorage.getItem('auxito_vehicles') || 'null'); } catch (error) { stored = null; }
      const saved = stored ? [stored.current].concat(stored.previous || []).filter((v) => v && v.year && v.make && v.model).slice(0, 3) : [];
      garageGrid.querySelectorAll('[data-garage-card]').forEach((old) => old.remove());
      saved.forEach((v) => {
        const hit = cG.match(v.year, v.make, v.model);
        const card = document.createElement('div');
        card.className = 'card';
        card.setAttribute('data-garage-card', '');
        card.innerHTML = '<h3></h3><p>Saved vehicle</p>';
        card.querySelector('h3').textContent = hit ? cG.fields.name(hit) : (v.display || [v.year, v.make, v.model].join(' '));
        if (hit) {
          claim(card, 'link');
          bindActivate(card, () => { cG.saveVehicle(hit); location.href = cG.withVehicle('?view=proto-shell-03', hit); });
        } else {
          claim(card, 'link');
          bindActivate(card, () => { location.href = legacyUrl(v); });
        }
        garageGrid.appendChild(card);
      });
      garageGrid.hidden = !saved.length;
    };
    renderGarage();

    /* 记录 14：+ Add Vehicle 弹窗 + 最新排第一、满 3 个替换最旧 */
    const modal = root.querySelector('[data-vehicle-modal]');
    /* 运营 2026-10-13：弹窗的 Make / Model 不限于上线车型，按全站车型目录（与横幅同一份）联动；
       保存后都存进 My Garage（不跳转），点 My Garage 卡片时上线车型进 03、其他车型进旧版车型页。车型列表加载中禁止保存，避免「新年份 + 旧车型」 */
    let modalModelSeq = 0;
    const modalLoading = () => modalFields.length === 3 && modalFields[2].disabled;
    if (useCatalog && modal && modalFields.length === 3) {
      const [my, mm, md] = modalFields;
      const modalModels = async () => {
        const seq = ++modalModelSeq;
        const year = val(my);
        const make = val(mm);
        if (!year || !make) { setOptions(md, [], 'Model', ''); return; }
        md.disabled = true;
        setOptions(md, [], 'Model', '');
        try {
          const data = await vu().getYearVehicleData(year);
          if (seq !== modalModelSeq) return;
          setOptions(md, vu().getAvailableModels(data, make).map((m) => ({ value: m, text: m.replace(/\bHd\b/g, 'HD') })), 'Model', '');
        } catch (error) {
          if (seq === modalModelSeq) { track('result', 'load_failed', { apsp_module: 'add-vehicle-modal' }); setOptions(md, [], 'Model', ''); }
        } finally {
          if (seq === modalModelSeq) md.disabled = false;
        }
      };
      /* 目录加载完之前先清空模板里的示例选项并禁止保存（PR #55 第十二轮审查：否则能保存 2023 / Ford / Civic 这类不存在的组合） */
      setOptions(my, [], 'Year', '');
      setOptions(mm, [], 'Make', '');
      setOptions(md, [], 'Model', '');
      md.disabled = true;
      ensureIndex().then(() => {
        setOptions(my, catYears().map((y) => ({ value: y })), 'Year', '');
        setOptions(mm, [], 'Make', '');
        setOptions(md, [], 'Model', '');
        md.disabled = false;
        my.addEventListener('change', () => { setOptions(mm, catMakes(val(my)).map((m) => ({ value: m })), 'Make', val(mm)); modalModels(); });
        mm.addEventListener('change', modalModels);
      }).catch(() => { track('result', 'load_failed', { apsp_module: 'add-vehicle-modal' }); md.disabled = false; if (c01) fillYmmSelects(modalFields, null); });
    }
    const addBtn = root.querySelector('.apsp-add-vehicle');
    const garageSection = root.querySelector('[data-apsp-module="my-garage"] h2');
    if (modal && addBtn && garageSection) {
      const grid = garageSection.closest('section').querySelector('.grid');
      const open = () => {
        modal.hidden = false;
        const first = modal.querySelector('select');
        if (first) first.focus();
      };
      const close = () => {
        modal.hidden = true;
      };
      claim(addBtn, 'button');
      bindActivate(addBtn, open);
      modal.querySelectorAll('[data-vehicle-modal-close]').forEach((btn) =>
        btn.addEventListener('click', close)
      );
      modal.addEventListener('click', (event) => {
        if (event.target === modal) close();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !modal.hidden) close();
      });
      const saveBtn = modal.querySelector('[data-vehicle-save]');
      if (saveBtn) {
        saveBtn.addEventListener('click', () => {
          const fields = [...modal.querySelectorAll('[data-vehicle-field]')];
          const value = fields.map((f) => f.value);
          if (modalLoading() || value.some((v) => !v || PLACEHOLDER.test(v))) return;
          /* 上线框架：保存进全站车型并按真实记录重画 My Garage */
          const cS = ctx();
          const picked = cS ? cS.match(value[0], value[1], value[2]) : null;
          track('vehicleSelected', 'garage_modal', value[0], value[1], value[2], picked ? 'yes' : (cS ? 'no' : ''));
          if (cS && picked) {
            cS.saveVehicle(picked);
            renderGarage();
            modal.hidden = true;
            return;
          }
          /* 非上线车型（运营 2026-10-13 第二次）：先存进全站车型（与页头 My Garage 同一份）并重画 My Garage，不直接跳转；
             用户点这张车型卡时才进旧版车型页（renderGarage 里非上线车型卡片的点击） */
          const store = window.Auxito && window.Auxito.Vehicle && window.Auxito.Vehicle.Storage;
          if (useCatalog && store && typeof store.addVehicle === 'function') {
            try {
              store.addVehicle({ year: String(value[0]), make: value[1], model: value[2] });
              window.dispatchEvent(new CustomEvent('auxito:vehicle-context-changed', { detail: { vehicle: store.load().current } }));
              renderGarage();
              close();
              return;
            } catch (error) { /* 存不了（隐私模式等）就按下面的页面内卡片处理 */ }
          }
          const name = value.join(' ');
          grid.querySelectorAll('[data-garage-card]').forEach((old) => {
            const title = old.querySelector('h3');
            if (title && norm(title.textContent) === norm(name)) old.remove();
          });
          const card = document.createElement('div');
          card.className = 'card apsp-garage-new';
          card.setAttribute('data-garage-card', '');
          card.innerHTML = '<h3></h3><p>Saved vehicle</p>';
          card.querySelector('h3').textContent = name;
          grid.insertBefore(card, grid.firstElementChild);
          /* 最新的排第一，超过 3 台删掉排在最后的（最早保存的） */
          const cards = grid.querySelectorAll('[data-garage-card]');
          for (let i = cards.length - 1; i >= 3; i -= 1) cards[i].remove();
          close();
        });
      }
    }
  }

  /* ---- 02 页 ---- */
  function wireScreen02(root) {
    const yearSel = root.querySelector('[data-model-year]');
    const exact = root.querySelector('[data-exact-vehicle]');
    const yearCard = root.querySelector('.apsp-model-year-card');
    const prompt = root.querySelector('[data-model-year-prompt]');
    let pendingGuide = null;
    let sheet = null;
    let backdrop = null;

    /* 02 页年份下拉的占位项文案是 Select Year，PLACEHOLDER 只认 Year/Make/Model */
    const yearChosen = () => {
      const value = yearSel ? yearSel.value.trim() : '';
      return !!value && !PLACEHOLDER.test(value) && !/^select year$/i.test(value);
    };

    /* 车型清单（上线框架）：年份下拉只列当前车型（品牌 + 车型）在清单里的年份 */
    const c02 = ctx();
    const modelVehicles = c02 ? c02.list.filter((v) => v.make === c02.current.make && v.model === c02.current.model) : [];
    if (c02 && yearSel) {
      yearSel.innerHTML = '<option>Select Year</option>';
      modelVehicles.map((v) => v.year).sort().reverse().forEach((y) => {
        const o = document.createElement('option'); o.value = y; o.textContent = y; yearSel.appendChild(o);
      });
      /* 只在访客存过这款车时预填年份（运营 2026-09-29：Guides 三个入口要先提示在顶部选 Year；以前按页面车型预填最新年份，点了直接跳走） */
      if (c02.saved && modelVehicles.some((v) => v.key === c02.saved.key)) yearSel.value = c02.saved.year;
    }
    const vehicleForYear = (year) => modelVehicles.filter((v) => v.year === year)[0] || null;

    /* Guides 前三卡：已选年份才带车型参数跳转（URLSearchParams 拼参，不手写 &）*/
    function guideUrl(base, year) {
      const v = vehicleForYear(year);
      if (c02 && v) return c02.withVehicle(base, v);
      const [path, hash] = base.split('#');
      const query = new URLSearchParams(path.split('?')[1] || '');
      query.set('year', year);
      query.set('make', 'Chevrolet');
      query.set('model', 'Silverado 2500 HD');
      return path.split('?')[0] + '?' + query.toString() + (hash ? '#' + hash : '');
    }

    function focusYear() {
      if (!yearSel) return;
      try {
        yearSel.focus({ preventScroll: true });
      } catch (error) {
        yearSel.focus();
      }
    }

    /* 没选年份：滚到顶部年份卡、显示提示 + 高亮/轻抖，焦点落到年份下拉 */
    function attention() {
      const card = yearCard;
      scrollToEl(card, { block: 'center' });
      if (prompt) prompt.hidden = false;
      if (card) {
        card.classList.add('apsp-ymm-highlight');
        if (!reduced()) {
          card.classList.remove('apsp-ymm-shake');
          void card.offsetWidth; /* 先移除再强制回流，shake 才能重复触发 */
          card.classList.add('apsp-ymm-shake');
        }
        window.setTimeout(() => {
          card.classList.remove('apsp-ymm-highlight', 'apsp-ymm-shake');
        }, 1600);
      }
      focusYear();
    }

    function ensureSheet() {
      if (sheet && sheet.isConnected) return sheet;
      backdrop = document.createElement('div');
      backdrop.className = 'apsp-sheet-backdrop';
      backdrop.hidden = true;
      sheet = document.createElement('div');
      sheet.className = 'apsp-ymm-sheet';
      sheet.hidden = true;
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-modal', 'true');
      sheet.setAttribute('aria-labelledby', 'apsp-model-year-sheet-title');
      sheet.innerHTML =
        '<h4 id="apsp-model-year-sheet-title">Select Your Year First</h4>' +
        '<p>Enter your Year to see options that fit your vehicle.</p>' +
        '<button class="btn full" type="button" data-model-year-sheet-btn>Select Model Year</button>';
      document.body.appendChild(backdrop);
      document.body.appendChild(sheet);
      backdrop.addEventListener('click', closeSheet);
      sheet.querySelector('[data-model-year-sheet-btn]').addEventListener('click', () => {
        closeSheet();
        scrollToEl(yearSel, { block: 'center' });
        focusYear();
        try {
          if (yearSel && typeof yearSel.showPicker === 'function') yearSel.showPicker();
        } catch (error) {
          /* 浏览器不允许脚本展开原生下拉时忽略，焦点已经落下 */
        }
      });
      return sheet;
    }
    function openSheet() {
      ensureSheet();
      backdrop.hidden = false;
      sheet.hidden = false;
    }
    function closeSheet() {
      if (sheet) sheet.hidden = true;
      if (backdrop) backdrop.hidden = true;
    }
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && sheet && !sheet.hidden) closeSheet();
    });

    /* 车型库这一年完全没有灯位数据的车（如 2022 Silverado 2500 HD）：选完年份后 Bulb Size & Fitment、Installation & Troubleshooting 两张卡直接隐藏（运营 2026-10-15 选方案一） */
    /* 同时删掉 Guides & Resources 标题，Contact Us 卡换成支持入口（07 帮助页同款深底样式，运营 2026-10-15）；其他年份 / 车型保持原样 */
    const dataCards = [...root.querySelectorAll('[data-guide-link]')].filter((card) => /proto-shell-0[67]\b/.test(card.getAttribute('data-guide-link')));
    const nodataSupport = root.querySelector('[data-guide-nodata-support]');
    const guidesSection = nodataSupport ? nodataSupport.closest('section') : null;
    const guidesHead = guidesSection ? guidesSection.querySelector('.section-head') : null;
    const contactCard = guidesSection ? guidesSection.querySelector('[data-guide-link*="get-in-touch"]') : null;
    function syncDataCards() {
      const v = yearChosen() ? vehicleForYear(yearSel.value.trim()) : null;
      const noData = !!(v && Array.isArray(v.fit) && !v.fit.length);
      dataCards.concat(contactCard && nodataSupport ? [contactCard] : [], guidesHead && nodataSupport ? [guidesHead] : []).forEach((el) => {
        el.hidden = noData;
        el.style.display = noData ? 'none' : '';
      });
      if (nodataSupport) nodataSupport.hidden = !noData;
      if (noData && pendingGuide && /proto-shell-0[67]\b/.test(pendingGuide)) pendingGuide = null;
    }
    if (yearSel) {
      yearSel.addEventListener('change', syncDataCards);
      syncDataCards();
      /* 浏览器返回（往返缓存恢复）时下拉可能保留上次的年份，按当前值重新同步 */
      window.addEventListener('pageshow', (event) => { if (event.persisted) syncDataCards(); });
    }

    root.querySelectorAll('[data-guide-link]').forEach((card) => {
      const base = card.getAttribute('data-guide-link');
      releaseInner(card);
      claim(card, 'link');
      bindActivate(card, () => {
        if (card.hasAttribute('data-guide-external')) {
          location.href = base;
          return;
        }
        if (!yearChosen()) {
          pendingGuide = base;
          if (window.matchMedia('(max-width: 600px)').matches) openSheet();
          else attention();
          return;
        }
        location.href = guideUrl(base, yearSel.value.trim());
      });
    });

    /* 选年份后 View Exact Vehicle 带上年份；有 pendingGuide 时改去指南页 */
    if (yearSel && exact) {
      const base = '?view=proto-shell-03';
      /* 运营 2026-10-22：没选年份时按钮显示为不可用、不带链接（不会进任何 03），点击只提示 Select a model year；选完年份才进对应 03 */
      const syncExactHref = () => {
        const v = yearChosen() ? vehicleForYear(yearSel.value.trim()) : null;
        if (!yearChosen()) {
          exact.removeAttribute('href');
          exact.setAttribute('aria-disabled', 'true');
          exact.setAttribute('role', 'link');
          exact.setAttribute('tabindex', '0');
          return;
        }
        exact.removeAttribute('aria-disabled');
        exact.removeAttribute('role');
        exact.removeAttribute('tabindex');
        exact.setAttribute('href', c02 && v ? c02.withVehicle(base, v) : base + '&year=' + encodeURIComponent(yearSel.value.trim()));
      };
      yearSel.addEventListener('change', () => {
        syncExactHref();
        if (yearChosen() && prompt) prompt.hidden = true;
      });
      /* 进页面预填：sessionStorage 里是 Chevrolet / Silverado 2500 HD 且年份在下拉里 */
      const saved = readYmm();
      if (saved && saved.make === 'Chevrolet' && saved.model === 'Silverado 2500 HD') {
        const match = [...yearSel.options].find((option) => option.value === saved.year);
        if (match) {
          yearSel.value = match.value;
          if (prompt) prompt.hidden = true;
        }
      }
      syncExactHref();
      syncDataCards(); /* 预填改了年份后再同步一次支持入口显隐（PR #56 审查） */
      exact.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !yearChosen()) { event.preventDefault(); attention(); }
      });
      exact.addEventListener('click', (event) => {
        /* 没选年份不跳转（原来会进 ?view=proto-shell-03，等于刷新 02），提示先选年份 */
        if (!yearChosen()) {
          event.preventDefault();
          attention();
          return;
        }
        if (!pendingGuide) return; /* 没有待跳指南时保持原链接（href 已带 year）*/
        event.preventDefault();
        location.href = guideUrl(pendingGuide, yearSel.value.trim());
      });
    }
    /* 视频卡封面失败时退成中性底，不留死图 */
    root.querySelectorAll('[data-video-poster]').forEach((img) => {
      img.addEventListener('error', () => {
        const media = img.closest('.apsp-video-media');
        if (media) media.classList.add('apsp-video-media--noimg');
        img.remove();
      });
    });
  }

  /* ---- BUG127：视频卡点击出弹窗（02 Builds & Installs、03 Real Installs）----
     参考主页 #video-modal：标题栏（返回箭头 / 居中标题 / 关闭 ×）+ 16:9 YouTube。
     - 卡片仍是指向 YouTube 的真链接：Ctrl/⌘/Shift/中键照常新开页，脚本失效时也能看。
     - 只有可信 YouTube 源、当前 iframe、当前 player id 的 onReady 才揭示播放器（iframe 的 load
       不算成功——错误页也会触发 load）；10s 无响应换错误态：Retry（换新 iframe 重新握手）+ 去 YouTube 看。
     - Esc / 遮罩 / 返回 / × 关闭：销毁 iframe 停止播放，解除滚动锁，焦点回到点开的卡片。
     - 运营 2026-09-29「字比画面先出现」：onReady 后视频第一帧还没到，会先看到黑屏 + 字幕。加载层改用视频封面，
       一直盖到播放器报告正在播放（state 1）；自动播放被拦时 onReady 后 2.5s 也揭开，用户可以自己点播放。
       鼠标移到 / 聚焦 / 触摸视频卡时预连 YouTube，缩短打开等待。 */
  const VM_ORIGIN = 'https://www.youtube.com';
  const VM_TIMEOUT = 10000;
  const VM_REVEAL_FALLBACK = 2500;
  let vmWarm = false;
  function warmYouTube() {
    if (vmWarm) return;
    vmWarm = true;
    ['https://www.youtube.com', 'https://i.ytimg.com', 'https://www.google.com'].forEach((href) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = '';
      document.head.appendChild(link);
    });
  }
  let vmodal = null;

  function startSeconds(href) {
    try {
      const raw = new URL(href, location.href).searchParams.get('t') || '';
      const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/.exec(raw);
      if (!match) return 0;
      return (Number(match[1]) || 0) * 3600 + (Number(match[2]) || 0) * 60 + (Number(match[3]) || 0);
    } catch (error) {
      return 0;
    }
  }

  function ensureVideoModal() {
    if (vmodal && vmodal.el.isConnected) return vmodal;
    const el = document.createElement('div');
    el.className = 'apsp-vmodal';
    el.hidden = true;
    el.innerHTML =
      '<div class="apsp-vmodal__overlay" data-vmodal-close></div>' +
      '<div class="apsp-vmodal__box" role="dialog" aria-modal="true" aria-labelledby="apsp-vmodal-title">' +
      '<div class="apsp-vmodal__head">' +
      '<button type="button" class="apsp-vmodal__btn" data-vmodal-close aria-label="Back">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7"/></svg></button>' +
      '<h3 class="apsp-vmodal__title" id="apsp-vmodal-title"></h3>' +
      '<button type="button" class="apsp-vmodal__btn" data-vmodal-close aria-label="Close">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg></button>' +
      '</div>' +
      '<div class="apsp-vmodal__body">' +
      '<div class="apsp-vmodal__frame"></div>' +
      '<div class="apsp-vmodal__state" role="status" aria-live="polite">' +
      '<img class="apsp-vmodal__poster" alt="" hidden>' +
      '<span class="apsp-vmodal__spinner" aria-hidden="true"></span>' +
      '<p class="apsp-vmodal__msg">Loading video…</p>' +
      '<div class="apsp-vmodal__actions">' +
      '<button type="button" class="apsp-vmodal__retry" hidden>Retry</button>' +
      '<a class="apsp-vmodal__source" href="https://www.youtube.com/" target="_blank" rel="noopener">Watch on YouTube ↗</a>' +
      '</div></div></div></div>';
    document.body.appendChild(el);

    const state = {
      el,
      frame: el.querySelector('.apsp-vmodal__frame'),
      title: el.querySelector('.apsp-vmodal__title'),
      msg: el.querySelector('.apsp-vmodal__msg'),
      retry: el.querySelector('.apsp-vmodal__retry'),
      source: el.querySelector('.apsp-vmodal__source'),
      poster: el.querySelector('.apsp-vmodal__poster'),
      iframe: null,
      playerId: '',
      attempt: 0,
      timers: [],
      embedUrl: '',
      returnFocus: null,
      prevOverflow: '',
    };

    const clearTimers = () => {
      state.timers.forEach((timer) => window.clearTimeout(timer));
      state.timers = [];
    };
    const listen = () => {
      if (!state.iframe || !state.iframe.contentWindow || el.hidden) return;
      state.iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'listening', id: state.playerId, channel: 'widget' }),
        VM_ORIGIN
      );
    };
    const reveal = () => {
      clearTimers();
      el.classList.add('is-playing');
    };
    state.poster.addEventListener('error', () => { state.poster.hidden = true; });
    const unavailable = () => {
      if (el.classList.contains('is-ready')) return;
      el.classList.add('is-unavailable');
      state.msg.textContent = "The video player didn't respond.";
      state.retry.hidden = false;
    };
    state.play = () => {
      clearTimers();
      el.classList.remove('is-ready', 'is-playing', 'is-unavailable');
      state.msg.textContent = 'Loading video…';
      state.retry.hidden = true;
      state.attempt += 1;
      state.playerId = 'apsp-vmodal-player-' + state.attempt;
      /* 每次重试都换一个新 iframe，给同一个 iframe 重写同样的 URL 不会重新加载 */
      const iframe = document.createElement('iframe');
      iframe.title = state.title.textContent || 'Video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.setAttribute('allowfullscreen', '');
      iframe.addEventListener('load', listen);
      state.frame.replaceChildren(iframe);
      state.iframe = iframe;
      iframe.src = state.embedUrl;
      state.timers = [0, 600, 1600, 3200, 5600].map((delay) => window.setTimeout(listen, delay));
      state.timers.push(window.setTimeout(unavailable, VM_TIMEOUT));
    };
    state.close = () => {
      if (el.hidden) return;
      clearTimers();
      el.hidden = true;
      el.classList.remove('is-ready', 'is-playing', 'is-unavailable');
      state.playerId = '';
      state.frame.replaceChildren(); /* 销毁 iframe = 停止播放 */
      state.iframe = null;
      document.body.style.overflow = state.prevOverflow;
      if (state.returnFocus && typeof state.returnFocus.focus === 'function') {
        try {
          state.returnFocus.focus({ preventScroll: true });
        } catch (error) {
          state.returnFocus.focus();
        }
      }
    };

    el.querySelectorAll('[data-vmodal-close]').forEach((btn) => btn.addEventListener('click', state.close));
    state.retry.addEventListener('click', state.play);
    document.addEventListener('keydown', (event) => {
      if (el.hidden) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        state.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...el.querySelectorAll('button, a[href], iframe')].filter(
        (node) => !node.hidden && node.getClientRects().length
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !el.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !el.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    });
    window.addEventListener('message', (event) => {
      if (el.hidden || !state.iframe || !state.playerId) return;
      if (event.origin !== VM_ORIGIN || event.source !== state.iframe.contentWindow) return;
      let payload = event.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch (error) {
          return;
        }
      }
      if (!payload || payload.id !== state.playerId) return;
      if (payload.event === 'onReady') {
        clearTimers();
        el.classList.remove('is-unavailable');
        el.classList.add('is-ready');
        state.timers.push(window.setTimeout(reveal, VM_REVEAL_FALLBACK));
        return;
      }
      const info = payload.info;
      const playing =
        (payload.event === 'onStateChange' && Number(info) === 1) ||
        (payload.event === 'infoDelivery' && info && info.playerState === 1);
      if (playing) track('videoPlaying', state.trackId, state.trackTitle, state.returnFocus);
      if (playing && el.classList.contains('is-ready')) reveal();
    });
    vmodal = state;
    return state;
  }

  function openVideoModal(card) {
    const id = card.getAttribute('data-video-id');
    if (!id) return;
    const modal = ensureVideoModal();
    const heading = card.querySelector('h3');
    const lines = heading ? [...heading.querySelectorAll('.apsp-video-title__line')] : [];
    const title = (lines.length ? lines.map((line) => line.textContent.trim()).join(' ') : heading ? heading.textContent : '')
      .replace(/\s+/g, ' ')
      .trim();
    const start = startSeconds(card.getAttribute('href') || '');
    const query = new URLSearchParams({
      autoplay: '1',
      rel: '0',
      playsinline: '1',
      enablejsapi: '1',
      origin: location.origin,
    });
    if (start) query.set('start', String(start));
    modal.embedUrl = VM_ORIGIN + '/embed/' + encodeURIComponent(id) + '?' + query.toString();
    modal.title.textContent = title || 'Video';
    const cardPoster = card.querySelector('[data-video-poster]');
    modal.poster.src = (cardPoster && cardPoster.naturalWidth && cardPoster.currentSrc) || 'https://i.ytimg.com/vi/' + encodeURIComponent(id) + '/hqdefault.jpg';
    modal.poster.hidden = false;
    modal.source.href = card.getAttribute('href') || 'https://www.youtube.com/watch?v=' + id;
    modal.returnFocus = card;
    modal.trackId = id;
    modal.trackTitle = title;
    track('moduleView', 'video-modal', 'Video'); /* 弹窗打开即算曝光（点击率分母） */
    modal.prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modal.el.hidden = false;
    const closeBtn = modal.el.querySelectorAll('.apsp-vmodal__btn')[1];
    if (closeBtn) {
      try {
        closeBtn.focus({ preventScroll: true });
      } catch (error) {
        closeBtn.focus();
      }
    }
    modal.play(); /* 在点击任务里设 src，YouTube 才会认自动播放 */
  }

  function wireVideoCards(root) {
    root.querySelectorAll('a.apsp-video-card[data-video-id]').forEach((card) => {
      if (card.__apspVideo) return;
      card.__apspVideo = true;
      card.setAttribute('aria-haspopup', 'dialog');
      ['pointerenter', 'focus', 'touchstart'].forEach((type) => card.addEventListener(type, warmYouTube, { once: true, passive: true }));
      card.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        openVideoModal(card);
      });
    });
  }

  /* 车型页的分类卡链接带 ?vehicle=<年份页 handle>，分类页据此显示 Home › 车型 › 分类（snippets/breadcrumbs.liquid）；
     这里只把正式车型名记下来（handle 还原不了 F-150 这类写法），不影响是否显示 */
  function rememberVehicleForCollections(root) {
    root.addEventListener('click', (event) => {
      const a = event.target.closest && event.target.closest('a[href^="/collections/"]');
      const c = ctx();
      if (!a || !c || !c.current) return;
      const yh = new URL(a.getAttribute('href'), location.origin).searchParams.get('vehicle');
      if (!yh) return;
      try {
        const names = JSON.parse(sessionStorage.getItem('apsp:vehicle-names') || '{}') || {};
        const hit = c.list.filter((v) => v.yh === yh)[0];
        if (hit) names[yh] = c.fields.name(hit);
        sessionStorage.setItem('apsp:vehicle-names', JSON.stringify(names));
      } catch (error) {
        /* 存不了就按 handle 还原车型名 */
      }
    });
  }

  function wireScreens(root) {
    const screen = root.getAttribute('data-apsp-screen') || '';
    rememberVehicleForCollections(root);
    wireVideoCards(root);
    if (screen === '01') wireScreen01(root);
    if (screen === '02') wireScreen02(root);
    if (screen === '04') wirePositionCards(root);
    if (screen === '06') wireFitmentGuide(root);
    if (screen === '05') {
      /* 04/06 带来的灯位上下文：只做溯源标记，不据此改写本页文案（本页只覆盖大灯） */
      const position = new URLSearchParams(location.search).get('position');
      if (position) root.setAttribute('data-apsp-position', position);
    }
  }

  /* ------------------------------------------------------------------ *
   * 对外接口
   * ------------------------------------------------------------------ */
  /* 09 / 10 套装加购（用户 2026-09-29 确认接真实购物车）：一次写入多个精确 Variant（同 Variant 合并数量），
     成功后打开站内侧边购物车，侧边购物车不可用时进 /cart。写入失败（如售罄）整单不加，返回 Shopify 的错误说明由页面显示。 */
  /* properties（可选）：给本次加入的每一行写购物车行属性（09 Buy individually 用 _apsp_solo 标出单独购买，不算进套装） */
  function addItems(items, trigger, properties) {
    const merged = new Map();
    (items || []).forEach((item) => {
      const id = Number(item && item.id);
      const quantity = Math.max(1, Number(item && item.quantity) || 1);
      if (id) merged.set(id, (merged.get(id) || 0) + quantity);
    });
    if (!merged.size) return Promise.reject(new Error('Your kit is empty.'));
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [...merged].map(([id, quantity]) => (properties ? { id, quantity, properties } : { id, quantity })) }),
    })
      .catch((error) => { track('result', 'add_to_cart_failed', { apsp_item: 'network' }); throw error; })
      .then((res) => res.json().catch(() => ({})).then((data) => {
        if (!res.ok) {
          track('result', 'add_to_cart_failed', { apsp_item: String(data.description || data.message || res.status).slice(0, 80) });
          throw new Error(data.description || data.message || 'Could not add to cart. Please try again.');
        }
        return data;
      }))
      .then((data) => {
        /* 埋点（assets/auxito-proto-shell-tracking.js）：Builder 加购金额与件数；购物车写 __apsp_builder（私有属性），订单里能看出是哪个 Builder 带来的 */
        const T = window.AuxitoTrack;
        if (T) {
          try {
            /* 按本次请求的件数 × 单价算（/cart/add.js 返回的是该行累计数量与金额，购物车里原来就有时会高估；PR #55 第六轮审查） */
            const lines = (data && data.items) || [];
            const unit = new Map(lines.map((l) => [Number(l.variant_id || l.id), Number(l.final_price != null ? l.final_price : l.price) || 0]));
            const value = [...merged].reduce((s, [id, q]) => s + q * (unit.get(id) || 0), 0) / 100;
            const qty = [...merged].reduce((s, [, q]) => s + q, 0);
            const page = T.context().apsp_page;
            T.send('apsp_add_to_cart', { value, currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD', item_count: qty });
            if (typeof T.markBuilder === 'function') T.markBuilder(page); /* 购物车刷新时写 __apsp_builder（assets/auxito-cart-drawer.js） */
          } catch (error) { /* 埋点失败不影响加购 */ }
        }
        const drawer = window.Auxito && window.Auxito.CartDrawer;
        if (drawer && typeof drawer.fetchCart === 'function' && typeof drawer.openCart === 'function') {
          return drawer.fetchCart(true).then((cart) => drawer.openCart(cart, trigger || null)).catch(() => { window.location.href = '/cart'; });
        }
        window.location.href = '/cart';
        return null;
      });
  }

  window.AuxitoProtoShellInteractions = {
    addItems,
    reveal,
    collapse,
    focusHeading,
    scrollToEl,
    reduced,
    claim,
    gotoView,
  };

  function boot() {
    const roots = document.querySelectorAll('.apsp');
    if (!roots.length) return;
    roots.forEach((root) => {
      markInert(root);
      keyboardize(root);
      watchChecked(root);
      wireScreens(root);
    });
    bindInertGuards();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
